import { RouteProps } from 'react-router';
import { CompartmentManagement } from './CompartmentManagement/CompartmentManagement';
import {
  AllInbox,
  Apps,
  CallSplit,
  Checklist,
  DashboardCustomize,
  DeveloperBoard,
  DevicesOther,
  Edit,
  Engineering,
  HowToVote,
  ImageSearch,
  InsertChartOutlined,
  MenuOpen,
  MoveToInbox,
  Receipt,
  SettingsSuggest,
  Store,
  TabUnselected,
  TouchApp,
  VideoLabel,
  ViewCarousel,
  WbIridescent,
  Webhook,
} from '@mui/icons-material';
import { ReactNode } from 'react';
import { TemplateManagement } from './TemplateManagement/TemplateManagement';
import { PresetManagement } from './PresetManagement/PresetManagement';
import { LabelManagement } from './LabelManagement/LabelManagement';
import { SystemStatus } from './SystemStatus/SystemStatus';
import { CompartmentSelectorManagement } from './CompartmentSelectorManagement/CompartmentSelectorManagement';
import { LinkProfileManagement } from './LinkProfileManagement/LinkProfileManagement';
import { FinderManagement } from './FinderManagement/FinderManagement';
import { OperationGroups } from './OperationGroups/OperationGroups';
import { UpdaterProfileManagement } from './UpdaterProfileManagement/UpdaterProfileManagement';
import { DevicePoolManagement } from './DevicePoolManagement/DevicePoolManagement';
import { ZoneManagement } from './ZoneManagement/ZoneManagement';
import { AllBlueprintsManagement } from './BlueprintManagement/AllBlueprintsManagement';
import { PickerProfileManagement } from './PickerProfileManagement/PickerProfileManagement';
import { AreaManagement } from './AreaManagement/AreaManagement';
import { RoleManagement } from './RoleManagement/RoleManagement';
import { TaskManagement } from './TaskManagement/TaskManagement';
import { UserManagement } from './UserManagement/UserManagement';
import { EventRuleManagement } from './EventRuleManagement/EventRuleManagement';
import { ReplenishmentPlanManagement } from './ReplenishmentPlan/ReplenishmentPlanManagement';
import { ReceivingAreaManagement } from './ReceivingArea/ReceivingAreaManagement';
import { SupplyProfileManagement } from './SupplyProfiles/SupplyProfileManagement';
import { SystemConfig } from './SystemConfig/SystemConfig';
import Permissions from './Common/Permissions';
import { PickingAreas } from './Picking/PickingAreas';
import { PickingSides } from './Picking/PickingSides';

export enum SubMenu {
  LABELING = 'Shelf Labeling',
  UNIT_CONFIG = 'Utility Components',
  TESTING = 'Danger Zone',
  SYSTEM = 'System Configuration',
  PICKINGV2 = 'Picking',
  EKANBAN = 'e-Kanban',
  LEGACY = 'Legacy',
}

/**
 * Sidebar render order. This is the source of truth for "where does each
 * submenu sit by default" — independent of the order entries happen to
 * appear in `PageAccessMap` below (a developer detail) and independent of
 * the enum declaration order (also a developer detail).
 *
 * LEGACY is always last so deprecated entries stay visually de-emphasized
 * at the bottom of the navigation.
 */
export const SUBMENU_ORDER: SubMenu[] = [
  SubMenu.LABELING,
  SubMenu.UNIT_CONFIG,
  SubMenu.PICKINGV2,
  SubMenu.EKANBAN,
  SubMenu.SYSTEM,
  SubMenu.TESTING,
  SubMenu.LEGACY,
];

export interface MenuEntryDef {
  subMenu: SubMenu;
  icon: ReactNode;
  label: string;
}

export interface PageAccess {
  /**
   * Sidebar placement. Optional: an entry with a `route` but no `menu` is
   * reachable by URL only and never appears in the navigation (used to
   * retire a page from the sidebar while keeping it accessible).
   */
  menu?: MenuEntryDef;
  route: RouteProps;
  permission: Permissions;
  disabled?: boolean;
}

/**
 * Maps und bundles pages that are accessible via menu entry with their route and required permission.
 */
export const PageAccessMap: Record<string, PageAccess> = {
  compartments: {
    permission: Permissions.SHELFLABELING_READ,
    route: {
      path: '/compartments',
      element: <CompartmentManagement />,
    },
    menu: {
      subMenu: SubMenu.LABELING,
      icon: <AllInbox />,
      label: 'Compartments',
    },
  },
  templates: {
    permission: Permissions.TEMPLATES_READ,
    route: {
      path: '/templates',
      element: <TemplateManagement />,
    },
    menu: {
      subMenu: SubMenu.LABELING,
      icon: <TabUnselected />,
      label: 'Templates',
    },
  },
  presets: {
    permission: Permissions.PRESETS_WRITE,
    route: {
      path: '/presets',
      element: <PresetManagement />,
    },
    menu: {
      subMenu: SubMenu.LABELING,
      icon: <MenuOpen />,
      label: 'Presets',
    },
  },
  labels: {
    permission: Permissions.SHELFLABELING_READ,
    route: {
      path: '/labels',
      element: <LabelManagement />,
    },
    menu: {
      subMenu: SubMenu.LABELING,
      icon: <VideoLabel />,
      label: 'Labels',
    },
  },
  appliances: {
    permission: Permissions.SYSTEMSTATUS_READ,
    route: {
      path: '/system',
      element: <SystemStatus />,
    },
    menu: {
      subMenu: SubMenu.LABELING,
      icon: <InsertChartOutlined />,
      label: 'Appliances',
    },
  },
  compartmentSelectors: {
    permission: Permissions.COMPARTMENT_SELECTORS_READ,
    route: {
      path: '/compartmentselectors',
      element: <CompartmentSelectorManagement />,
    },
    menu: {
      icon: <CallSplit />,
      subMenu: SubMenu.UNIT_CONFIG,
      label: 'Compartment Selectors',
    },
  },
  linkProfiles: {
    permission: Permissions.LINK_PROFILES_WRITE,
    route: {
      path: '/linkprofiles',
      element: <LinkProfileManagement />,
    },
    menu: {
      subMenu: SubMenu.UNIT_CONFIG,
      icon: <ViewCarousel />,
      label: 'Link Profiles',
    },
  },
  finders: {
    permission: Permissions.FINDERS_READ,
    route: {
      path: '/finders',
      element: <FinderManagement />,
    },
    menu: {
      subMenu: SubMenu.UNIT_CONFIG,
      icon: <ImageSearch />,
      label: 'Finders',
    },
  },
  operationGroups: {
    permission: Permissions.OPERATION_GROUPS_READ,
    route: {
      path: '/operationgroups',
      element: <OperationGroups />,
    },
    menu: {
      subMenu: SubMenu.LEGACY,
      icon: <DeveloperBoard />,
      label: 'Operation Groups',
    },
  },
  updaterProfiles: {
    permission: Permissions.UPDATER_PROFILE_READ,
    route: {
      path: '/updaterprofiles',
      element: <UpdaterProfileManagement />,
    },
    menu: {
      subMenu: SubMenu.UNIT_CONFIG,
      icon: <Edit />,
      label: 'Updater Profiles',
    },
  },
  devicePools: {
    permission: Permissions.DEVICE_POOLS_READ,
    route: {
      path: '/picking/devicepools',
      element: <DevicePoolManagement />,
    },
    menu: {
      subMenu: SubMenu.PICKINGV2,
      icon: <DevicesOther />,
      label: 'Device Pools',
    },
  },
  zones: {
    permission: Permissions.ZONES_READ,
    route: {
      path: '/picking/zones',
      element: <ZoneManagement />,
    },
    menu: {
      subMenu: SubMenu.PICKINGV2,
      icon: <WbIridescent />,
      label: 'Zones',
    },
  },
  blueprints: {
    permission: Permissions.BLUEPRINTS_READ,
    route: {
      path: '/picking/blueprints',
      element: <AllBlueprintsManagement />,
    },
    menu: {
      subMenu: SubMenu.PICKINGV2,
      icon: <Receipt />,
      label: 'Blueprints',
    },
  },
  pickerProfiles: {
    permission: Permissions.PICKER_PROFILES_READ,
    route: {
      path: '/pickerprofiles',
      element: <PickerProfileManagement />,
    },
    menu: {
      subMenu: SubMenu.PICKINGV2,
      icon: <TouchApp />,
      label: 'Picker Profiles',
    },
  },
  pickingSides: {
    permission: Permissions.PICKING_AREAS_READ,
    route: {
      path: '/pickingsides',
      element: <PickingSides />,
    },
    menu: {
      subMenu: SubMenu.LEGACY,
      icon: <HowToVote />,
      label: 'Sides (Picking V1)',
    },
  },
  pickingAreas: {
    permission: Permissions.PICKING_AREAS_READ,
    route: {
      path: '/pickingareas',
      element: <PickingAreas />,
    },
    menu: {
      subMenu: SubMenu.LEGACY,
      icon: <Store />,
      label: 'Areas (Picking V1)',
    },
  },
  nodes: {
    permission: Permissions.AREAS_READ,
    route: {
      path: '/areas',
      element: <AreaManagement />,
    },
    menu: {
      subMenu: SubMenu.SYSTEM,
      icon: <Apps />,
      label: 'Areas',
    },
  },
  // Hidden from the sidebar — Settings → Administration → Roles is the
  // primary UI now. This legacy page stays reachable only by entering
  // /roles directly. No `menu` entry, so it renders no nav item.
  roles: {
    permission: Permissions.ROLES_READ,
    route: {
      path: '/roles',
      element: <RoleManagement />,
    },
  },
  tasks: {
    permission: Permissions.TASKS_READ,
    route: {
      path: '/tasks/*',
      element: <TaskManagement />,
    },
    menu: {
      subMenu: SubMenu.SYSTEM,
      icon: <Engineering />,
      label: 'Tasks',
    },
  },
  // Hidden from the sidebar — Settings → Administration → Users is the
  // primary UI now. This legacy page stays reachable only by entering
  // /users directly. No `menu` entry, so it renders no nav item.
  users: {
    permission: Permissions.USERS_READ,
    route: {
      path: '/users/*',
      element: <UserManagement />,
    },
  },
  eventRules: {
    permission: Permissions.EVENT_RULES_READ,
    route: {
      path: '/eventrules',
      element: <EventRuleManagement />,
    },
    menu: {
      subMenu: SubMenu.SYSTEM,
      icon: <Webhook />,
      label: 'Event Rules',
    },
  },
  ekanbanPlans: {
    permission: Permissions.EKANBAN_READ,
    route: {
      path: '/ekanban/replenishment-plans',
      element: <ReplenishmentPlanManagement />,
    },
    menu: {
      subMenu: SubMenu.EKANBAN,
      icon: <Checklist />,
      label: 'Replenishment Plans',
    },
  },
  ekanbanAreas: {
    permission: Permissions.EKANBAN_READ,
    route: {
      path: '/ekanban/receiving-areas',
      element: <ReceivingAreaManagement />,
    },
    menu: {
      subMenu: SubMenu.EKANBAN,
      icon: <MoveToInbox />,
      label: 'Receiving Areas',
    },
  },
  ekanbanProfiles: {
    permission: Permissions.EKANBAN_READ,
    route: {
      path: '/ekanban/supply-profiles',
      element: <SupplyProfileManagement />,
    },
    menu: {
      subMenu: SubMenu.EKANBAN,
      icon: <DashboardCustomize />,
      label: 'Supply Profiles',
    },
  },
  systemConfig: {
    permission: Permissions.SHELFLABELING_READ,
    route: {
      path: '/system-config',
      element: <SystemConfig />,
    },
    menu: {
      subMenu: SubMenu.SYSTEM,
      icon: <SettingsSuggest />,
      label: 'System Configuration',
    },
  },
};
